import React, { FormEventHandler, useCallback, useRef, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { ReservationRate } from '~/services/ReservationService';

import { CollectionReason, RefundMetadata, VendorRefundTypes } from '../../types';
import CaseIDAndReasons from '../Common/CaseIDAndReasons';
import EditableShareOfRefund from '../Common/EditableShareOfRefund';
import MarkCancelled from '../Common/MarkCancelled';
import RefundSurcharge from '../Common/RefundSurcharge';
import ShareOfRefund from '../Common/ShareOfRefund';
import { getCostPrice, getCostPriceInCostCurrency } from '../Utils/RefundUtils';
import { DEFAULT_SURCHARGE_TYPES } from '../Utils/SurchargeDataHook';

import BNBLSelectNights from './CustomRefund/BNBLSelectNights';
import CustomAmount from './CustomRefund/CustomAmount';
import SelectNights from './CustomRefund/SelectNights';

export enum RefundTabs {
  FULL_REFUND_WITH_FEE,
  CUSTOM_REFUND,
}

interface Props {
  checkIn?: string;
  checkOut?: string;
  currencyCode: string;
  decrementStep: () => void;
  defaultCaseId: string;
  defaultComment: string;
  handleLateChangeOfMindFieldChange: (formData, selectNights?: Set<string>, preferStoredPercentage?: boolean) => void;
  hasSurcharge?: boolean;
  LEContribution: number;
  onSubmit: FormEventHandler<HTMLFormElement>;
  reason: CollectionReason;
  refundMetadata: RefundMetadata;
  reservationId?: string;
  surchargeAmount?: number;
  vendorContribution: number;
  selectedNights?: Set<string>;
  onSelectedNightsChange?: (newCheckedDates: Set<string>) => void;
  itemId?: string;
  reservationRates?: Array<ReservationRate>;
  isConvertingCostPrice?: boolean;
  disableCustomAmount?: boolean;
  selectedSurchargeTypes?: Array<string>;
  onSelectedSurchargeTypesChange?: (types: Array<string>) => void;
  onSurchargeToggleChange?: (formData) => void;
  handleCustomVendorContribution: (newValue: string) => void;
  groupRefundCeiling?: number;
}

export default function LateChangeOfMindBadExperienceOrUnfortunateEvents({
  checkIn,
  checkOut,
  currencyCode,
  decrementStep,
  defaultCaseId,
  defaultComment,
  handleLateChangeOfMindFieldChange,
  hasSurcharge = false,
  LEContribution,
  onSubmit,
  reason,
  refundMetadata,
  reservationId,
  surchargeAmount,
  vendorContribution,
  selectedNights,
  onSelectedNightsChange,
  itemId,
  reservationRates,
  isConvertingCostPrice,
  disableCustomAmount = false,
  selectedSurchargeTypes = [...DEFAULT_SURCHARGE_TYPES],
  onSelectedSurchargeTypesChange,
  onSurchargeToggleChange,
  handleCustomVendorContribution,
  groupRefundCeiling,
}: Props) {
  const isBNBL = !checkIn && !checkOut;
  const [feeOrCustom, setFeeOrCustom] = useState<RefundTabs>(RefundTabs.FULL_REFUND_WITH_FEE);
  const [percentageOrNumber, setPercentageOrNumber] = useState<VendorRefundTypes>(VendorRefundTypes.percentage);
  const [customerRefundAmount, setCustomerRefundAmount] = useState('');
  const [surchargeRefundedToCustomer, setSurchargeRefundedToCustomer] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const maxRefundAmount = groupRefundCeiling ?? refundMetadata.item_metadata.cash_amount;
  const maxSurchargeRefundAmount = refundMetadata.surcharge_metadata?.cash_amount || 0;
  const costPrice = getCostPrice(refundMetadata);
  const costCurrencyCode = refundMetadata?.package_cost_price_data?.cost_price_currency || '';
  const currenciesDiffer = Boolean(costCurrencyCode && currencyCode && costCurrencyCode !== currencyCode);
  const costPriceInCostCurrency = getCostPriceInCostCurrency(refundMetadata);
  const handleFeeOrCustomChange = useCallback((_, value: RefundTabs) => {
    setFeeOrCustom(value);
  }, []);

  const handlePercentageOrNumberChange = useCallback(
    (_, value: VendorRefundTypes) => {
      const formData = new FormData(formRef.current);
      setPercentageOrNumber(value);
      formData.set('percentageOrNumber', value);
      if (value === VendorRefundTypes.percentage) {
        // values will default to 100%
        formData.set('percentageRefundToCustomer', maxRefundAmount.toString());
        formData.set('percentageRefund', '100');
        // ensure Select Nights values are reset to safe defaults
        formData.set('numberOfNights', '0');
        formData.set('totalRefundedToCustomer', '0');
        formData.set('surchargeRefundedToCustomer', '0');
        // clear any previously selected nights to avoid Select Nights branch being taken
        if (onSelectedNightsChange) {
          onSelectedNightsChange(new Set());
        }
      } else {
        // values will default to 0
        formData.set('numberOfNights', '0');
        formData.set('totalRefundedToCustomer', '0');
        formData.set('surchargeRefundedToCustomer', '0');
      }
      handleLateChangeOfMindFieldChange(formData);
    },
    [handleLateChangeOfMindFieldChange, maxRefundAmount, onSelectedNightsChange],
  );

  const handleCustomerRefundAmountChange = useCallback((newAmount: string) => {
    setCustomerRefundAmount(newAmount);
  }, []);

  const handleSurchargeToggleChange = useCallback(() => {
    const formData = new FormData(formRef.current);
    onSurchargeToggleChange(formData);
  }, [onSurchargeToggleChange]);

  return (
    <>
      <DialogContent dividers>
        <form onSubmit={onSubmit} ref={formRef} id="late-change-of-mind-form">
          <CaseIDAndReasons reason={reason} defaultComment={defaultComment} defaultCaseId={defaultCaseId} />
          {/* Full Refund With Fee, Or Custom Refund */}
          <Stack spacing={1}>
            <Box>
              <Tabs value={feeOrCustom} onChange={handleFeeOrCustomChange}>
                <Tab
                  label="Full Refund with Fee"
                  value={RefundTabs.FULL_REFUND_WITH_FEE}
                  data-cy="Full Refund With Fee"
                />
                <Tab label="Custom Refund" value={RefundTabs.CUSTOM_REFUND} data-cy="Custom Refund" />
              </Tabs>
              {/* Hidden input to hold the feeOrCustom tab value */}
              <input type="hidden" name="feeOrCustom" value={feeOrCustom} />
            </Box>
            {feeOrCustom === RefundTabs.FULL_REFUND_WITH_FEE && (
              <>
                <Stack>
                  <TextField
                    type="number"
                    name="refundFee"
                    label="Fee Charged to Customer"
                    data-cy="FeeChargedToCustomer"
                    required
                    inputProps={{
                      min: 0,
                      max: maxRefundAmount,
                      step: '.01',
                    }}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  {hasSurcharge && (
                    <RefundSurcharge
                      surchargeAmount={surchargeAmount}
                      currencyCode={currencyCode}
                      defaultShouldSurchargeBeRefunded="true"
                      onChange={handleSurchargeToggleChange}
                    />
                  )}
                </Stack>
              </>
            )}
            {feeOrCustom === RefundTabs.CUSTOM_REFUND && (
              <>
                <FormControl>
                  <RadioGroup
                    name="percentageOrNumber"
                    row
                    value={percentageOrNumber}
                    onChange={handlePercentageOrNumberChange}
                  >
                    <FormControlLabel
                      data-cy="CustomAmount"
                      value={VendorRefundTypes.percentage}
                      control={
                        <Radio
                          inputProps={{
                            required: true,
                          }}
                        />
                      }
                      label="Custom Amount"
                    />
                    <FormControlLabel
                      key="select-nights"
                      value={VendorRefundTypes.number}
                      data-cy="SelectNights"
                      control={
                        <Radio
                          inputProps={{
                            required: true,
                          }}
                        />
                      }
                      label="Select Nights"
                    />
                  </RadioGroup>
                </FormControl>
                {percentageOrNumber === VendorRefundTypes.percentage && (
                  <>
                    <CustomAmount
                      maxRefundAmount={maxRefundAmount}
                      formRef={formRef}
                      handleLateChangeOfMindFieldChange={handleLateChangeOfMindFieldChange}
                      onCustomerRefundAmountChange={handleCustomerRefundAmountChange}
                      currencyCode={currencyCode}
                    />
                    <Divider variant="fullWidth" />
                    <Typography variant="h6" marginTop={2}>
                      Share of Refund
                    </Typography>
                    <EditableShareOfRefund
                      vendorContribution={vendorContribution}
                      LEContribution={LEContribution}
                      onVendorContributionChange={handleCustomVendorContribution}
                      costPrice={currenciesDiffer ? costPriceInCostCurrency : costPrice}
                      costPriceSell={costPrice}
                      customerRefundAmount={customerRefundAmount}
                      currencyCode={currencyCode}
                      vendorCurrencyCode={currenciesDiffer ? costCurrencyCode : currencyCode}
                      isConvertingCostPrice={isConvertingCostPrice}
                    />
                    <Stack spacing={2} direction="column">
                      {hasSurcharge && (
                        <RefundSurcharge
                          surchargeAmount={surchargeAmount}
                          currencyCode={currencyCode}
                          onChange={handleSurchargeToggleChange}
                        />
                      )}
                    </Stack>
                  </>
                )}

                {percentageOrNumber === VendorRefundTypes.number && (
                  <>
                    {isBNBL && (
                      <BNBLSelectNights
                        refundMetadata={refundMetadata}
                        numberOfNights={refundMetadata.number_of_nights}
                        currencyCode={currencyCode}
                        formRef={formRef}
                        handleLateChangeOfMindFieldChange={handleLateChangeOfMindFieldChange}
                      />
                    )}
                    {!isBNBL && (
                      <SelectNights
                        refundMetadata={refundMetadata}
                        checkInDate={checkIn}
                        checkOutDate={checkOut}
                        currencyCode={currencyCode}
                        formRef={formRef}
                        reservationId={reservationId}
                        maxRefundAmount={maxRefundAmount}
                        maxSurchargeRefundAmount={maxSurchargeRefundAmount}
                        handleLateChangeOfMindFieldChange={handleLateChangeOfMindFieldChange}
                        selectedNights={selectedNights}
                        setSelectedNights={onSelectedNightsChange}
                        surchargeRefundedToCustomer={surchargeRefundedToCustomer}
                        setSurchargeRefundedToCustomer={setSurchargeRefundedToCustomer}
                        itemId={itemId}
                        reservationRates={reservationRates}
                        disableCustomAmount={disableCustomAmount}
                        selectedSurchargeTypes={selectedSurchargeTypes}
                        onSelectedSurchargeTypesChange={onSelectedSurchargeTypesChange}
                      />
                    )}
                    <Divider variant="fullWidth" />
                    <Typography variant="h6" marginTop={2}>
                      Share of Refund
                    </Typography>
                    <ShareOfRefund
                      vendorContribution={vendorContribution}
                      LEContribution={LEContribution}
                      currencyCode={currencyCode}
                      isConvertingCostPrice={isConvertingCostPrice}
                    />
                  </>
                )}
              </>
            )}
            <Stack spacing={2} direction="column">
              <MarkCancelled />
            </Stack>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={decrementStep}>Back</Button>
        <Button type="submit" variant="contained" form="late-change-of-mind-form">
          Continue
        </Button>
      </DialogActions>
    </>
  );
}
